import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { dayLabel, hhmm, type ApiError } from '@limuzin/kit';
import { Icon } from '../material';
import { pickPhotos, shrink } from '../photos';
import { type OutboxMessage } from '../outbox';
import { Button, ConfirmSheet, Sheet, showProblem, useErrorHaptic } from '../widgets';
import { PhotoSource } from './profile';
import { fullDate } from '../build';

/**
 * The conversation, as limuzin_core's `chat.dart`, `chat_media.dart`,
 * `chat_photos.dart`, `chat_photo_viewer.dart`, `chat_photo_sketch.dart` and
 * `chat_selection.dart` draw it — shared, as there, by the ride's chat and by
 * «Поддержка»: a header with the other side in it, the city drawn quietly behind,
 * a day's heading over its first message, bubbles that carry photographs and voice,
 * and one box to say the next thing in.
 */

// ------------------------------------------------------------------- the frame

export interface Selection {
  active: boolean;
  count: number;
  busy: boolean;
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  clear: () => void;
  remove: () => void;
}

/**
 * The screen: a header over a fixed wallpaper, with whatever scrolls on top of it.
 * While messages are being chosen the header counts them instead, with a ✕ to stop
 * and a bin to delete them.
 */
export function ChatLayout({
  title,
  subtitle,
  avatar,
  support = false,
  action,
  selection,
  onBack,
  children,
}: {
  title: string;
  subtitle?: string;
  /** The person on the other side, 36 across — their face, or their initials. */
  avatar?: ReactNode;
  /** «Поддержка»: a headset on yellow where a face would be. */
  support?: boolean;
  action?: ReactNode;
  selection: Selection;
  onBack: () => void;
  children: ReactNode;
}) {
  const selecting = selection.active;
  return (
    <div className="page chat">
      <header className="chat-bar">
        <button
          type="button"
          className="icon-btn"
          aria-label={selecting ? 'Отменить выбор' : 'Назад'}
          disabled={selection.busy}
          onClick={selecting ? selection.clear : onBack}
        >
          <Icon name={selecting ? 'close' : 'arrow_back_ios_new_rounded'} size={20} />
        </button>
        {selecting ? (
          <span className="chat-bar-title grow">Выбрано: {selection.count}</span>
        ) : (
          <>
            {avatar ?? (
              <span className={support ? 'chat-face support' : 'chat-face'}>
                <Icon name={support ? 'headset_mic_outlined' : 'person_outline_rounded'} size={22} />
              </span>
            )}
            <span className="grow" style={{ marginLeft: 10 }}>
              <span className="chat-bar-title">{title}</span>
              {subtitle && <span className="chat-bar-sub">{subtitle}</span>}
            </span>
          </>
        )}
        {selecting ? (
          <button
            type="button"
            className="icon-btn"
            aria-label="Удалить выбранные сообщения"
            title="Удалить выбранные сообщения"
            disabled={selection.busy}
            onClick={selection.remove}
          >
            {selection.busy ? <span className="spinner" style={{ width: 20, height: 20 }} /> : <Icon name="delete_outline" size={24} />}
          </button>
        ) : (
          action
        )}
      </header>
      <div className="chat-body">
        <Wallpaper />
        {children}
      </div>
    </div>
  );
}

/**
 * Quiet city line art behind the messages, fixed while they scroll: a road, a taxi
 * with its roof light, a skyline, a pin, a tree — `_CityWallpaper`, tile for tile,
 * in the accent at a sixth of its strength.
 */
function Wallpaper() {
  return (
    <svg className="chat-wallpaper" aria-hidden="true">
      <defs>
        <pattern id="city" width="280" height="300" x="-30" y="-40" patternUnits="userSpaceOnUse">
          <g fill="none" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: 'var(--chat-line)' }}>
            <path d="M0 25C85 5 53 90 133 83C240 70 189 145 280 137" />
            <rect x="28" y="130" width="49" height="23" rx="7" />
            <path d="M34 130 41 118H64L71 130" />
            <path d="M34 139H40M65 139H71M36 154V158M69 154V158" />
            <rect x="167" y="42" width="13" height="43" />
            <rect x="184" y="19" width="13" height="66" />
            <rect x="201" y="36" width="13" height="49" />
            <path d="M172 49h3M172 59h3M172 69h3M172 79h3M189 26h3M189 36h3M189 46h3M189 56h3M189 66h3M189 76h3M206 43h3M206 53h3M206 63h3M206 73h3" />
            <path d="M158 86H223" />
            <path d="M112 245C79 238 100 209 111 198C128 216 139 241 112 245Z" />
            <path d="M112 219V258M112 237 104 229" />
          </g>
          <g fill="none" strokeWidth="1.7" strokeLinecap="round" style={{ stroke: 'var(--chat-route)' }}>
            <path d="M-10 110C80 100 35 200 125 187C235 170 160 285 290 265" strokeDasharray="9 7" />
            <path d="M49 114H57" />
            <path d="M237 190C228 177 215 163 226 153C242 140 260 156 249 171Z" />
            <circle cx="237" cy="160" r="4" />
          </g>
          <g style={{ fill: 'var(--chat-route)' }}>
            <rect x="34" y="254" width="9" height="9" />
            <rect x="43" y="245" width="9" height="9" />
            <rect x="52" y="254" width="9" height="9" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#city)" />
    </svg>
  );
}

// ------------------------------------------------------------------- history

/** «25 сентября», with the year when it is not this one. */
function dayHeading(at: Date): string {
  const full = fullDate(at);
  return at.getFullYear() === new Date().getFullYear() ? full.slice(0, full.lastIndexOf(' ')) : full;
}

const dayOf = (at: string) => new Date(at).toDateString();

/**
 * A chronological history with one heading per local day. A message that leaves the
 * list fades and closes up where it stood rather than vanishing. While the newest
 * message is in view it stays there: a photograph growing to its size or the
 * keyboard opening pushes the older ones up, not it down.
 */
export function ChatHistory<M>({
  rows,
  id,
  sentAt,
  render,
  departs = () => true,
  empty,
}: {
  rows: M[];
  id: (row: M) => string;
  sentAt: (row: M) => string;
  render: (row: M) => ReactNode;
  /** Whether a row leaving is really gone, rather than replaced by the server's copy. */
  departs?: (row: M) => boolean;
  empty: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const atEnd = useRef(true);
  const previous = useRef<M[]>(rows);
  const [leaving, setLeaving] = useState<M[]>([]);
  const timers = useRef<number[]>([]);

  // What went, kept for the moment it takes to fade and close. Each departure has
  // its own timer, which a reload arriving in the meantime does not cancel — or
  // the row would stay, faded and folded, for good.
  useEffect(() => {
    const present = new Set(rows.map(id));
    const gone = previous.current.filter((row) => !present.has(id(row)) && departs(row));
    previous.current = rows;
    if (gone.length === 0) return;
    setLeaving((current) => [...current, ...gone]);
    const ids = new Set(gone.map(id));
    timers.current.push(
      window.setTimeout(() => {
        setLeaving((current) => current.filter((row) => !ids.has(id(row))));
      }, 360),
    );
    // `id` and `departs` are the caller's accessors, the same on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), []);

  const shown = useMemo(() => {
    const all = [...rows];
    for (const row of leaving) {
      const at = Date.parse(sentAt(row));
      const index = all.findIndex((other) => Date.parse(sentAt(other)) > at);
      all.splice(index < 0 ? all.length : index, 0, row);
    }
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, leaving]);

  const keepEnd = useCallback(() => {
    const el = box.current;
    if (el && atEnd.current) el.scrollTop = el.scrollHeight;
  }, []);

  useLayoutEffect(keepEnd, [shown, keepEnd]);

  useEffect(() => {
    const el = content.current;
    if (!el) return;
    const watch = new ResizeObserver(keepEnd);
    watch.observe(el);
    return () => watch.disconnect();
  }, [keepEnd]);

  if (shown.length === 0) return <div className="chat-empty">{empty}</div>;

  const leavingIds = new Set(leaving.map(id));
  const staying = new Set(rows.map((row) => dayOf(sentAt(row))));

  return (
    <div
      ref={box}
      className="chat-history"
      onScroll={(event) => {
        const el = event.currentTarget;
        atEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
      }}
    >
      <div ref={content} className="chat-column">
        {shown.map((row, index) => {
          const day = dayOf(sentAt(row));
          const startsDay = index === 0 || day !== dayOf(sentAt(shown[index - 1]!));
          const going = leavingIds.has(id(row));
          // A day's heading leaves with its last message.
          const heading = startsDay && (!going || staying.has(day)) && (
            <div className="chat-day">
              <span>{dayHeading(new Date(sentAt(row)))}</span>
            </div>
          );
          return (
            <div key={id(row)}>
              {!going && heading}
              <div className={going ? 'chat-row leaving' : 'chat-row'}>
                {going && startsDay && !staying.has(day) && (
                  <div className="chat-day">
                    <span>{dayHeading(new Date(sentAt(row)))}</span>
                  </div>
                )}
                {render(row)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- bubbles

/** One picture in a conversation, as its bubble and the viewer both show it. */
export interface ChatPhoto {
  /** Unique in the conversation. */
  id: string;
  /** «Вы», «Водитель», «Поддержка» — the viewer's header names them. */
  author: string;
  sentAt: string;
  caption: string;
  load: () => Promise<Blob>;
}

/** A voice message, fetched through the session before it plays. */
export interface ChatVoice {
  id: string;
  load: () => Promise<Blob>;
}

export type SendStatus = 'sending' | 'sent' | 'failed';

export function Bubble({
  mine,
  text,
  sentAt,
  support = false,
  status = 'sent',
  onFailedTap,
  photos = [],
  voices = [],
}: {
  mine: boolean;
  text: string;
  sentAt: string;
  /** An answer from «Поддержка», on a wash of the accent rather than on white. */
  support?: boolean;
  /** Only ours carries one: what the other side did with theirs is not told. */
  status?: SendStatus;
  onFailedTap?: () => void;
  photos?: ChatPhoto[];
  voices?: ChatVoice[];
}) {
  const said = text.length > 0 || voices.length > 0;
  const classes = ['bubble', mine ? 'mine' : support ? 'support' : 'theirs', photos.length > 0 ? 'framed' : ''];
  const stamp = (onPhoto: boolean) => (
    <span className={onPhoto ? 'bubble-stamp on-photo' : 'bubble-stamp'}>
      {hhmm(new Date(sentAt))}
      {mine && (
        <>
          {/* The tick says the server has it — no more: there are no delivery or
              read receipts to draw. */}
          {status === 'sending' && <Icon name="schedule_rounded" size={14} label="Отправляется" style={{ opacity: 0.7 }} />}
          {status === 'sent' && <Icon name="check_rounded" size={16} label="Отправлено" />}
          {status === 'failed' && (
            <Icon name="error_outline_rounded" size={16} label="Не отправлено" style={{ color: '#FFB3B9' }} />
          )}
        </>
      )}
    </span>
  );
  const words = (
    <>
      {voices.length > 0 && (
        <div className="bubble-voices">
          {voices.map((voice) => (
            <VoicePlayer key={voice.id} voice={voice} />
          ))}
        </div>
      )}
      {text && <p className="bubble-text">{text}</p>}
      {stamp(false)}
    </>
  );
  const bubble = (
    <div className={classes.filter(Boolean).join(' ')}>
      {photos.length === 0 ? (
        words
      ) : (
        <>
          <PhotoBlock photos={photos} stamp={said ? null : stamp(true)} />
          {said && <div className="bubble-under">{words}</div>}
        </>
      )}
    </div>
  );
  const line = <div className={mine ? 'bubble-line mine' : 'bubble-line'}>{bubble}</div>;
  if (!onFailedTap) return line;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Не отправлено. Нажмите, чтобы повторить или удалить"
      onClick={onFailedTap}
      style={{ cursor: 'pointer' }}
    >
      {line}
    </div>
  );
}

/** A message still going out, drawn as it will be once it has gone. */
export function OutboxBubble({
  message,
  author = 'Вы',
  onFailedTap,
}: {
  message: OutboxMessage;
  author?: string;
  onFailedTap?: () => void;
}) {
  return (
    <Bubble
      mine
      text={message.text}
      sentAt={message.sentAt}
      status={message.failed ? 'failed' : 'sending'}
      onFailedTap={message.failed ? onFailedTap : undefined}
      photos={outboxPhotos(message, author)}
      voices={message.voice ? message.files.map((file, i) => ({ id: `${message.id}-${i}`, load: async () => file })) : []}
    />
  );
}

export function outboxPhotos(message: OutboxMessage, author = 'Вы'): ChatPhoto[] {
  if (message.voice) return [];
  return message.files.map((file, i) => ({
    id: `${message.id}-${i}`,
    author,
    sentAt: message.sentAt,
    caption: message.text,
    load: async () => file,
  }));
}

/** «Сообщение не отправлено» — what a tap on one offers. */
export function FailedSheet({
  onRetry,
  onDiscard,
  onClose,
}: {
  onRetry: () => void;
  onDiscard: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet title="Сообщение не отправлено" onClose={onClose}>
      <div className="stack" style={{ gap: 10 }}>
        <Button size="card" tone="tint" icon="refresh" onClick={onRetry}>
          Повторить
        </Button>
        <Button size="card" tone="stop" icon="delete_outline" onClick={onDiscard}>
          Удалить
        </Button>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------- photos

interface Picture {
  url: string;
  /** Width over height, as decoded. */
  aspect: number;
}

/**
 * Photos fetched once between the bubble and the viewer, the forty most recently
 * asked for. A fetch that fails is forgotten, so the next look tries again.
 */
const pictures = new Map<string, Promise<Picture>>();

function loadPicture(photo: ChatPhoto): Promise<Picture> {
  let held = pictures.get(photo.id);
  if (held) {
    pictures.delete(photo.id);
    pictures.set(photo.id, held);
    return held;
  }
  held = photo.load().then(
    (blob) =>
      new Promise<Picture>((done, fail) => {
        const url = URL.createObjectURL(blob);
        const image = new Image();
        image.onload = () => done({ url, aspect: image.naturalWidth / Math.max(1, image.naturalHeight) });
        image.onerror = () => {
          URL.revokeObjectURL(url);
          fail(new Error('undecodable'));
        };
        image.src = url;
      }),
  );
  held.catch(() => pictures.delete(photo.id));
  pictures.set(photo.id, held);
  while (pictures.size > 40) {
    const oldest = pictures.keys().next().value!;
    const going = pictures.get(oldest);
    pictures.delete(oldest);
    void going?.then((picture) => URL.revokeObjectURL(picture.url)).catch(() => {});
  }
  return held;
}

function usePicture(photo: ChatPhoto): { picture: Picture | null; failed: boolean; retry: () => void } {
  const [state, setState] = useState<{ picture: Picture | null; failed: boolean }>({
    picture: null,
    failed: false,
  });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    loadPicture(photo)
      .then((picture) => live && setState({ picture, failed: false }))
      .catch(() => live && setState({ picture: null, failed: true }));
    return () => {
      live = false;
    };
    // Keyed on the photo's id: the loader is a new closure every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id, attempt]);
  return {
    ...state,
    retry: () => {
      setState({ picture: null, failed: false });
      setAttempt((n) => n + 1);
    },
  };
}

/**
 * Where each photo sits in its bubble, `max` wide at most: one keeps its shape in a
 * box a quarter taller than it is wide, and an album is rows of squares, under one
 * wide photo when the count is odd — `chatPhotoCells`.
 */
function cells(aspects: (number | null)[], max: number) {
  const gap = 2;
  if (aspects.length === 1) {
    const aspect = aspects[0] ?? 4 / 3;
    const tallest = max * 1.25;
    return [
      aspect >= max / tallest
        ? { left: 0, top: 0, width: max, height: Math.max(max / aspect, 100) }
        : { left: 0, top: 0, width: Math.min(Math.max(tallest * aspect, 160), max), height: tallest },
    ];
  }
  const out: { left: number; top: number; width: number; height: number }[] = [];
  let top = 0;
  if (aspects.length % 2 === 1) {
    out.push({ left: 0, top: 0, width: max, height: max * 0.6 });
    top = max * 0.6 + gap;
  }
  const half = (max - gap) / 2;
  while (out.length < aspects.length) {
    out.push({ left: 0, top, width: half, height: half });
    out.push({ left: half + gap, top, width: half, height: half });
    top += half + gap;
  }
  return out.slice(0, aspects.length);
}

/** Every photo of the conversation on screen, for the viewer to page through. */
const Gallery = createContext<{ open: (photo: ChatPhoto, siblings: ChatPhoto[]) => void } | null>(null);

/**
 * The conversation's photographs, oldest first, and the viewer they open in: tap a
 * photo and the rest are a swipe away on either side. [send] is where a photo drawn
 * on goes; without it there is no «Рисовать».
 */
export function ChatGallery({
  photos,
  send,
  children,
}: {
  photos: ChatPhoto[];
  send?: (file: File) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState<{ list: ChatPhoto[]; index: number } | null>(null);
  const value = useMemo(
    () => ({
      open: (photo: ChatPhoto, siblings: ChatPhoto[]) => {
        const index = photos.findIndex((one) => one.id === photo.id);
        setOpen(index < 0 ? { list: siblings, index: Math.max(0, siblings.indexOf(photo)) } : { list: photos, index });
      },
    }),
    [photos],
  );
  return (
    <Gallery.Provider value={value}>
      {children}
      {open &&
        createPortal(
          <PhotoViewer photos={open.list} start={open.index} send={send} onClose={() => setOpen(null)} />,
          document.body,
        )}
    </Gallery.Provider>
  );
}

/** A message's photos, edge to edge inside its bubble. */
function PhotoBlock({ photos, stamp }: { photos: ChatPhoto[]; stamp: ReactNode }) {
  const gallery = useContext(Gallery);
  const [aspects, setAspects] = useState<(number | null)[]>(() => photos.map(() => null));
  // The bubble's width, less the frame of bubble colour round the photos.
  const max = Math.min(320, Math.round(Math.min(window.innerWidth, 720) * 0.82 - 32 - 6));
  const laid = cells(aspects, max);
  const width = Math.max(...laid.map((cell) => cell.left + cell.width));
  const height = Math.max(...laid.map((cell) => cell.top + cell.height));

  return (
    <div className="photo-block" style={{ width, height }}>
      {photos.map((photo, i) => (
        <PhotoTile
          key={photo.id}
          photo={photo}
          box={laid[i]!}
          onMeasured={(aspect) =>
            setAspects((current) => (current[i] === aspect ? current : current.map((one, at) => (at === i ? aspect : one))))
          }
          onOpen={() => gallery?.open(photo, photos)}
        />
      ))}
      {stamp && <span className="photo-stamp">{stamp}</span>}
    </div>
  );
}

function PhotoTile({
  photo,
  box,
  onMeasured,
  onOpen,
}: {
  photo: ChatPhoto;
  box: { left: number; top: number; width: number; height: number };
  onMeasured: (aspect: number) => void;
  onOpen: () => void;
}) {
  const { picture, failed, retry } = usePicture(photo);
  useEffect(() => {
    if (picture) onMeasured(picture.aspect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picture]);
  return (
    <span className="photo-tile" style={box}>
      {picture ? (
        <button type="button" className="photo-open" aria-label="Открыть фото" onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}>
          <img src={picture.url} alt="" />
        </button>
      ) : failed ? (
        <button type="button" className="icon-btn" aria-label="Загрузить фото снова" onClick={retry}>
          <Icon name="refresh" size={22} />
        </button>
      ) : (
        <span className="spinner" style={{ width: 22, height: 22 }} />
      )}
    </span>
  );
}

/** «Сегодня в 14:32», «13 сентября в 09:10». */
function when(at: string): string {
  const date = new Date(at);
  const day = date.getFullYear() === new Date().getFullYear() ? dayLabel(date) : fullDate(date);
  return `${day} в ${hhmm(date)}`;
}

/**
 * The photo viewer: swipe sideways for the conversation's other photos, tap to hide
 * the bars, «Рисовать» to send a drawn-on copy, and the download arrow to keep the
 * photo itself.
 */
function PhotoViewer({
  photos,
  start,
  send,
  onClose,
}: {
  photos: ChatPhoto[];
  start: number;
  send?: (file: File) => void;
  onClose: () => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(start);
  const [bars, setBars] = useState(true);
  const [drawing, setDrawing] = useState<Picture | null>(null);
  const photo = photos[index]!;
  const { picture } = usePicture(photo);

  useLayoutEffect(() => {
    const el = track.current;
    if (el) el.scrollLeft = start * el.clientWidth;
  }, [start]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (drawing) return;
      if (event.key === 'Escape') onClose();
      const el = track.current;
      if (!el) return;
      if (event.key === 'ArrowRight') el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
      if (event.key === 'ArrowLeft') el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, drawing]);

  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label="Фото">
      <div
        ref={track}
        className="viewer-track"
        onScroll={(event) => {
          const el = event.currentTarget;
          const at = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
          if (at !== index && at >= 0 && at < photos.length) setIndex(at);
        }}
      >
        {photos.map((one) => (
          <ViewerPage key={one.id} photo={one} onTap={() => setBars((shown) => !shown)} />
        ))}
      </div>

      <div className={bars ? 'viewer-bar top' : 'viewer-bar top hidden'}>
        <button type="button" className="icon-btn light" aria-label="Закрыть" onClick={onClose}>
          <Icon name="arrow_back" size={24} />
        </button>
        <span className="grow" style={{ marginLeft: 4 }}>
          <span className="viewer-author">{photo.author}</span>
          <span className="viewer-when">{when(photo.sentAt)}</span>
        </span>
        {photos.length > 1 && (
          <span className="viewer-count">
            {index + 1} из {photos.length}
          </span>
        )}
      </div>

      <div className={bars ? 'viewer-bar bottom' : 'viewer-bar bottom hidden'}>
        {photo.caption && <p className="viewer-caption">{photo.caption}</p>}
        <div className="row" style={{ gap: 0 }}>
          {send && (
            <button
              type="button"
              className="icon-btn light"
              aria-label="Рисовать"
              title="Рисовать"
              disabled={!picture}
              onClick={() => picture && setDrawing(picture)}
            >
              <Icon name="brush_rounded" size={24} />
            </button>
          )}
          <span className="grow" />
          {picture && (
            <a
              className="icon-btn light"
              href={picture.url}
              download={`limuzin-${Date.now()}.jpg`}
              aria-label="Сохранить фото"
              title="Сохранить фото"
            >
              <Icon name="download_rounded" size={24} />
            </a>
          )}
        </div>
      </div>

      {drawing && send && (
        <Sketch
          picture={drawing}
          onClose={() => setDrawing(null)}
          onSend={(file) => {
            send(file);
            setDrawing(null);
            onClose();
          }}
        />
      )}
    </div>
  );
}

function ViewerPage({ photo, onTap }: { photo: ChatPhoto; onTap: () => void }) {
  const { picture, failed, retry } = usePicture(photo);
  return (
    <div className="viewer-page" onClick={onTap}>
      {picture ? (
        <img src={picture.url} alt="" />
      ) : failed ? (
        <button type="button" className="icon-btn light" aria-label="Загрузить фото снова" onClick={(event) => {
          event.stopPropagation();
          retry();
        }}>
          <Icon name="refresh" size={26} />
        </button>
      ) : (
        <span className="spinner" style={{ width: 26, height: 26, color: '#fff' }} />
      )}
    </div>
  );
}

/** The brush colours, in Telegram's order, as the phone offers them. */
const SWATCHES: [string, string][] = [
  ['#FFFFFF', 'Белый'],
  ['#000000', 'Чёрный'],
  ['#FF3B30', 'Красный'],
  ['#FF9500', 'Оранжевый'],
  ['#FFCC00', 'Жёлтый'],
  ['#34C759', 'Зелёный'],
  ['#32ADE6', 'Голубой'],
  ['#AF52DE', 'Фиолетовый'],
];

interface Stroke {
  color: string;
  /** A fraction of the photo's width. */
  width: number;
  /** Fractions of the photo's width and height. */
  points: { x: number; y: number }[];
}

/**
 * Draws over a photo and sends the result as a new one; the photo drawn on stays as
 * it was. Strokes are kept in fractions of the photo, so what is sent is drawn at
 * the photo's own resolution rather than the screen's.
 */
function Sketch({
  picture,
  onClose,
  onSend,
}: {
  picture: Picture;
  onClose: () => void;
  onSend: (file: File) => void;
}) {
  const area = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const image = useRef<HTMLImageElement | null>(null);
  const [ready, setReady] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [color, setColor] = useState(SWATCHES[2]![0]);
  const [brush, setBrush] = useState(6);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [sending, setSending] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const drawing = useRef<Stroke | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      image.current = img;
      setReady(true);
    };
    img.src = picture.url;
  }, [picture.url]);

  // The photo at its unzoomed size: all of it in view, with a margin a stroke can
  // start in.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el || !ready) return;
    const fit = () => {
      const room = { width: Math.max(1, el.clientWidth - 24), height: Math.max(1, el.clientHeight - 24) };
      const height = Math.min(room.height, room.width / picture.aspect);
      setSize({ width: height * picture.aspect, height });
    };
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  }, [ready, picture.aspect]);

  const paint = useCallback(
    (target: HTMLCanvasElement, width: number, height: number, all: Stroke[]) => {
      const context = target.getContext('2d');
      const img = image.current;
      if (!context || !img) return;
      context.drawImage(img, 0, 0, width, height);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      for (const stroke of all) {
        context.strokeStyle = stroke.color;
        context.fillStyle = stroke.color;
        context.lineWidth = stroke.width * width;
        const [first, ...rest] = stroke.points;
        if (!first) continue;
        if (rest.length === 0) {
          context.beginPath();
          context.arc(first.x * width, first.y * height, (stroke.width * width) / 2, 0, Math.PI * 2);
          context.fill();
          continue;
        }
        context.beginPath();
        context.moveTo(first.x * width, first.y * height);
        for (const point of rest) context.lineTo(point.x * width, point.y * height);
        context.stroke();
      }
    },
    [],
  );

  useEffect(() => {
    const el = canvas.current;
    if (!el || size.width === 0) return;
    const ratio = window.devicePixelRatio || 1;
    el.width = Math.round(size.width * ratio);
    el.height = Math.round(size.height * ratio);
    paint(el, el.width, el.height, strokes);
  }, [size, strokes, paint]);

  const at = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: (event.clientX - box.left) / box.width, y: (event.clientY - box.top) / box.height };
  };

  const send = async () => {
    const img = image.current;
    if (!img) return;
    setSending(true);
    try {
      const scale = Math.min(1, 2560 / Math.max(img.naturalWidth, img.naturalHeight));
      const out = document.createElement('canvas');
      out.width = Math.round(img.naturalWidth * scale);
      out.height = Math.round(img.naturalHeight * scale);
      paint(out, out.width, out.height, strokes);
      const blob = await new Promise<Blob | null>((done) => out.toBlob(done, 'image/jpeg', 0.9));
      if (!blob) throw new Error('no blob');
      onSend(new File([blob], `sketch-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    } catch {
      showProblem('Не удалось отправить фото. Попробуйте ещё раз.');
      setSending(false);
    }
  };

  const leave = () => (strokes.length > 0 ? setLeaving(true) : onClose());

  return (
    <div className="sketch">
      <div className="row" style={{ padding: 'calc(4px + var(--safe-top)) 4px 0' }}>
        <button type="button" className="icon-btn light" aria-label="Закрыть" disabled={sending} onClick={leave}>
          <Icon name="close_rounded" size={24} />
        </button>
        <span className="grow" />
        <button
          type="button"
          className="icon-btn light"
          aria-label="Отменить штрих"
          title="Отменить штрих"
          disabled={strokes.length === 0 || sending}
          onClick={() => setStrokes((all) => all.slice(0, -1))}
        >
          <Icon name="undo_rounded" size={24} />
        </button>
      </div>
      <div ref={area} className="sketch-area">
        {ready ? (
          <canvas
            ref={canvas}
            style={{ width: size.width, height: size.height }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              const stroke = { color, width: brush / Math.max(1, size.width), points: [at(event)] };
              drawing.current = stroke;
              setStrokes((all) => [...all, stroke]);
            }}
            onPointerMove={(event) => {
              const stroke = drawing.current;
              if (!stroke) return;
              stroke.points.push(at(event));
              setStrokes((all) => [...all]);
            }}
            onPointerUp={() => {
              drawing.current = null;
            }}
            onPointerCancel={() => {
              drawing.current = null;
            }}
          />
        ) : (
          <span className="spinner" style={{ width: 26, height: 26, color: '#fff' }} />
        )}
      </div>
      <div className="sketch-swatches">
        {SWATCHES.map(([swatch, name]) => (
          <button
            key={swatch}
            type="button"
            aria-label={name}
            title={name}
            aria-pressed={swatch === color}
            onClick={() => setColor(swatch)}
          >
            <span className={swatch === color ? 'on' : ''} style={{ background: swatch }} />
          </button>
        ))}
      </div>
      <div className="row sketch-width">
        <Icon name="brush_rounded" size={16} />
        <input
          type="range"
          className="grow"
          min={2}
          max={24}
          step={0.5}
          value={brush}
          aria-label="Толщина кисти"
          style={{ accentColor: color === '#000000' ? '#FFFFFF' : color }}
          onChange={(event) => setBrush(Number(event.target.value))}
        />
        <Icon name="brush_rounded" size={26} />
      </div>
      <div style={{ padding: '4px 16px calc(12px + var(--safe-bottom))' }}>
        <Button size="field" icon="near_me_rounded" busy={sending} disabled={!ready} onClick={() => void send()}>
          Отправить в чат
        </Button>
      </div>
      {leaving && (
        <Sheet title="Выйти без отправки?" hint="Нарисованное пропадёт." onClose={() => setLeaving(false)}>
          <div className="stack" style={{ gap: 10 }}>
            <Button size="card" onClick={() => setLeaving(false)}>
              Рисовать дальше
            </Button>
            <Button size="card" tone="stop" onClick={onClose}>
              Выйти
            </Button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- voice

/**
 * Whoever is being listened to at this moment, across the whole conversation: a
 * second message starting pauses the first, which keeps its place and shows play.
 */
let playing: HTMLAudioElement | null = null;

/** Forty peaks of a recording, each from nought to one — `ChatWaveformCache`. */
const waveforms = new Map<string, Promise<number[]>>();

function waveformOf(id: string, blob: Blob): Promise<number[]> {
  let held = waveforms.get(id);
  if (held) return held;
  held = blob.arrayBuffer().then(async (bytes) => {
    const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const context = new Context();
    try {
      const audio = await context.decodeAudioData(bytes);
      const data = audio.getChannelData(0);
      const bars = Array.from({ length: 40 }, (_, index) => {
        const start = Math.floor((index * data.length) / 40);
        const end = Math.max(start + 1, Math.floor(((index + 1) * data.length) / 40));
        let peak = 0;
        for (let i = start; i < end; i += 16) peak = Math.max(peak, Math.abs(data[i] ?? 0));
        return peak;
      });
      const top = Math.max(...bars);
      return { bars: bars.map((bar) => (top === 0 ? 0 : bar / top)), seconds: audio.duration };
    } finally {
      void context.close();
    }
  }).then((result) => {
    durations.set(id, result.seconds);
    return result.bars;
  });
  held.catch(() => waveforms.delete(id));
  waveforms.set(id, held);
  return held;
}

/** What decoding measured, for a recording whose file does not say how long it is. */
const durations = new Map<string, number>();

const mmss = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/** A voice message: play, the shape of what was said, and how long it is. */
function VoicePlayer({ voice }: { voice: ChatVoice }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [position, setPosition] = useState(0);
  const [isPlaying, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bars, setBars] = useState<number[] | null>(null);

  // Fetched before the first tap, so the length is on show.
  const prepare = useCallback(async () => {
    if (audio.current) return audio.current;
    const blob = await voice.load();
    const url = URL.createObjectURL(blob);
    const element = new Audio(url);
    element.preload = 'metadata';
    element.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(element.duration)) setDuration(element.duration);
    });
    element.addEventListener('timeupdate', () => setPosition(element.currentTime));
    element.addEventListener('play', () => setPlaying(true));
    element.addEventListener('pause', () => setPlaying(false));
    element.addEventListener('ended', () => {
      if (playing === element) playing = null;
      setPlaying(false);
      setPosition(0);
    });
    audio.current = element;
    void waveformOf(voice.id, blob)
      .then((peaks) => {
        setBars(peaks);
        const measured = durations.get(voice.id);
        if (measured) setDuration((known) => known ?? measured);
      })
      .catch(() => {
        // A neutral line rather than made-up bars; playback still works.
      });
    return element;
  }, [voice]);

  useEffect(() => {
    void prepare().catch(() => {});
    return () => {
      const element = audio.current;
      if (!element) return;
      element.pause();
      if (playing === element) playing = null;
      URL.revokeObjectURL(element.src);
    };
    // Once, for this message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.id]);

  const play = async () => {
    setBusy(true);
    try {
      const element = await prepare();
      if (!element.paused) {
        element.pause();
        if (playing === element) playing = null;
      } else {
        if (playing && playing !== element) playing.pause();
        playing = element;
        await element.play();
      }
    } catch {
      showProblem('Не удалось воспроизвести запись. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  const progress = duration ? Math.min(1, position / duration) : 0;

  return (
    <span className="voice" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className="voice-play"
        aria-label={isPlaying ? 'Пауза' : 'Воспроизвести голосовое сообщение'}
        disabled={busy}
        onClick={() => void play()}
      >
        {busy ? (
          <span className="spinner" style={{ width: 20, height: 20 }} />
        ) : (
          <Icon name={isPlaying ? 'pause_rounded' : 'play_arrow_rounded'} size={28} />
        )}
      </button>
      <span className="grow">
        <svg className="voice-wave" viewBox="0 0 160 28" preserveAspectRatio="none" aria-hidden="true">
          {bars ? (
            bars.map((bar, i) => {
              const x = ((i + 0.5) * 160) / bars.length;
              const height = 2 + bar * 24;
              return (
                <line
                  key={i}
                  x1={x}
                  x2={x}
                  y1={(28 - height) / 2}
                  y2={(28 + height) / 2}
                  opacity={(i + 0.5) / bars.length <= progress ? 1 : 0.4}
                />
              );
            })
          ) : (
            <line x1="0" x2="160" y1="14" y2="14" opacity="0.25" />
          )}
        </svg>
        <span className="voice-time" aria-label={duration === null ? 'Длительность загружается' : `Длительность ${mmss(duration)}`}>
          {duration === null ? '—:—' : mmss(isPlaying || position > 0 ? position : duration)}
        </span>
      </span>
    </span>
  );
}

// -------------------------------------------------------------------- composer

/** The container a browser can record voice in that the server keeps: AAC in MP4. */
function recordable(): string | null {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null;
  for (const type of ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}

/**
 * The box a message is written in — Inter, as the bubble sets it — with a paper clip
 * for photos and, while nothing is written, a microphone.
 *
 * Owns the draft until something else takes it, and not a moment longer: «Отправить»
 * hands the message to the outbox and the box empties in the same frame.
 *
 * Voice is recorded only where the browser can write the one format the server keeps
 * — AAC in MP4, which Safari and recent Chrome can and Firefox cannot. Elsewhere the
 * microphone is not offered, and what arrives still plays.
 */
export function Composer({
  onSend,
  maxPhotos = 1,
}: {
  onSend: (text: string, files: File[], voice: boolean) => void;
  maxPhotos?: number;
}) {
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState<{ file: File; url: string }[]>([]);
  const [voice, setVoice] = useState<File | null>(null);
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [tenths, setTenths] = useState(0);
  const [error, setError] = useState<string | null>(null);
  useErrorHaptic(error);
  const [choosing, setChoosing] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const ending = useRef<'send' | 'cancel' | 'preview'>('preview');
  const format = useMemo(recordable, []);

  // The box grows with what is typed, up to five lines.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 5 * 22.4 + 24)}px`;
  }, [text]);

  // Photo previews are the browser holding a file open.
  const held = useRef(photos);
  held.current = photos;
  useEffect(() => () => held.current.forEach((photo) => URL.revokeObjectURL(photo.url)), []);

  const send = useCallback(
    (recorded?: File) => {
      const said = text.trim();
      const withVoice = recorded ?? voice;
      if (!said && photos.length === 0 && !withVoice) return;
      onSend(said, withVoice ? [withVoice] : photos.map((photo) => photo.file), Boolean(withVoice));
      photos.forEach((photo) => URL.revokeObjectURL(photo.url));
      setText('');
      setPhotos([]);
      setVoice(null);
      setError(null);
    },
    [text, photos, voice, onSend],
  );

  const finish = useCallback((end: 'send' | 'cancel' | 'preview') => {
    ending.current = end;
    const active = recorder.current;
    if (active && active.state !== 'inactive') active.stop();
  }, []);

  // A passenger who leaves the tab mid-sentence comes back to a recording to send
  // or throw away, not to one still running.
  useEffect(() => {
    if (!recording) return;
    const hidden = () => {
      if (document.visibilityState === 'hidden') finish('preview');
    };
    document.addEventListener('visibilitychange', hidden);
    const timer = window.setInterval(() => {
      setTenths((n) => {
        // Two minutes is a voice message; past that it is a phone call.
        if (n + 1 >= 1200) finish('preview');
        return n + 1;
      });
    }, 100);
    return () => {
      document.removeEventListener('visibilitychange', hidden);
      window.clearInterval(timer);
    };
  }, [recording, finish]);

  const record = async () => {
    if (!format || recording || starting) return;
    setStarting(true);
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setStarting(false);
      setError('Разрешите доступ к микрофону в настройках браузера.');
      return;
    }
    try {
      const chunks: Blob[] = [];
      const active = new MediaRecorder(stream, { mimeType: format, audioBitsPerSecond: 64000 });
      active.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      });
      active.addEventListener('stop', () => {
        stream.getTracks().forEach((track) => track.stop());
        recorder.current = null;
        setRecording(false);
        if (ending.current === 'cancel' || chunks.length === 0) return;
        // Named and typed as the server files it: an `.m4a`, `audio/mp4` and nothing
        // after the semicolon.
        const file = new File(chunks, `voice-${Date.now()}.m4a`, { type: 'audio/mp4' });
        if (ending.current === 'send') send(file);
        else setVoice(file);
      });
      recorder.current = active;
      ending.current = 'preview';
      active.start();
      setTenths(0);
      setRecording(true);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      setError('Не удалось начать запись. Попробуйте ещё раз.');
    } finally {
      setStarting(false);
    }
  };

  const attach = async (camera: boolean) => {
    setChoosing(false);
    const room = maxPhotos - photos.length;
    const chosen = await pickPhotos(camera ? { camera: 'environment' } : { multiple: room > 1 });
    const files = await Promise.all(chosen.slice(0, room).map((file) => shrink(file, 2000)));
    if (files.length === 0) return;
    setPhotos((current) => [...current, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
    setVoice(null);
    setError(null);
  };

  const empty = text.trim().length === 0 && photos.length === 0 && !voice;
  const canAttach = !voice && !recording && photos.length < maxPhotos;
  const desktop = typeof matchMedia === 'function' && matchMedia('(min-width: 720px)').matches;

  return (
    <div className="chat-column composer-wrap">
      {error && <p className="composer-error">{error}</p>}
      <div className="composer">
        {(photos.length > 0 || voice) && (
          <div className="composer-attachments">
            {photos.map((photo) => (
              <span key={photo.url} className="row" style={{ gap: 0 }}>
                <img className="composer-thumb" src={photo.url} alt="" />
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Удалить вложение"
                  onClick={() => {
                    URL.revokeObjectURL(photo.url);
                    setPhotos((current) => current.filter((one) => one !== photo));
                  }}
                >
                  <Icon name="close" size={24} />
                </button>
              </span>
            ))}
            {voice && (
              <span className="row" style={{ gap: 0, color: 'var(--chat-ink)' }}>
                <VoicePlayer voice={{ id: voice.name, load: async () => voice }} />
                <button type="button" className="icon-btn" aria-label="Удалить вложение" onClick={() => setVoice(null)}>
                  <Icon name="close" size={24} />
                </button>
              </span>
            )}
          </div>
        )}

        {recording || starting ? (
          <div className="composer-recording">
            <button
              type="button"
              className="icon-btn big recording-cancel"
              aria-label="Отменить запись"
              onClick={() => finish('cancel')}
            >
              <Icon name="delete_outline" size={25} />
            </button>
            <span className="recording-clock" aria-label={starting ? 'Подготовка записи' : 'Длительность записи'}>
              <span className="recording-dot" />
              {starting
                ? '0:00,0'
                : `${Math.floor(tenths / 600)}:${String(Math.floor(tenths / 10) % 60).padStart(2, '0')},${tenths % 10}`}
            </span>
            <button
              type="button"
              className="send-disc"
              aria-label="Отправить"
              disabled={starting}
              onClick={() => finish('send')}
            >
              <Icon name="near_me_rounded" size={25} />
            </button>
          </div>
        ) : (
          <div className="composer-row">
            <button
              type="button"
              className="icon-btn big composer-attach"
              aria-label="Прикрепить фото"
              title="Прикрепить фото"
              disabled={!canAttach}
              onClick={() => setChoosing(true)}
            >
              <Icon name="attach_file_rounded" size={26} style={{ transform: 'rotate(45deg)' }} />
            </button>
            <textarea
              ref={box}
              className="composer-input grow"
              rows={1}
              placeholder="Написать сообщение…"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends on a desktop keyboard; on a phone the return key makes a
                // newline, which is what a thumb expects.
                if (event.key === 'Enter' && !event.shiftKey && desktop) {
                  event.preventDefault();
                  send();
                }
              }}
            />
            {empty && format ? (
              <button
                type="button"
                className="send-disc"
                aria-label="Записать голосовое сообщение"
                title="Записать голосовое сообщение"
                onClick={() => void record()}
              >
                <Icon name="mic_none" size={25} />
              </button>
            ) : (
              <button type="button" className="send-disc" aria-label="Отправить" disabled={empty} onClick={() => send()}>
                <Icon name="near_me_rounded" size={25} />
              </button>
            )}
          </div>
        )}
      </div>
      {choosing && (
        <PhotoSource
          title="Приложить фото"
          onGallery={() => void attach(false)}
          onCamera={() => void attach(true)}
          onClose={() => setChoosing(false)}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------- selection

/**
 * Choosing messages to delete: a long press on one starts it, a tap then adds or
 * takes away, and the header counts them. Mine and theirs alike, a hundred at most.
 */
export function useSelection(
  ids: string[],
  confirmAndDelete: (ids: string[]) => Promise<boolean>,
): Selection {
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // What is no longer in the conversation is no longer chosen.
  const present = ids.join('|');
  useEffect(() => {
    setChosen((current) => {
      const keep = new Set([...current].filter((id) => ids.includes(id)));
      return keep.size === current.size ? current : keep;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [present]);

  return {
    active: chosen.size > 0,
    count: chosen.size,
    busy,
    has: (id) => chosen.has(id),
    toggle: (id) => {
      if (busy) return;
      setChosen((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else if (next.size >= 100) {
          showProblem('Можно выбрать до 100 сообщений за раз.');
          return current;
        } else next.add(id);
        return next;
      });
    },
    clear: () => {
      if (!busy) setChosen(new Set());
    },
    remove: () => {
      if (busy || chosen.size === 0) return;
      setBusy(true);
      void confirmAndDelete([...chosen])
        .then((gone) => {
          if (gone) setChosen(new Set());
        })
        .finally(() => setBusy(false));
    },
  };
}

/**
 * One message, choosable. The mark sits in the gutter a bubble leaves on its far
 * side, so the bubble keeps its width; only bubbles from the other side, which
 * start where the mark goes, step aside for it.
 */
export function Selectable({
  id,
  mine,
  selection,
  children,
}: {
  id: string;
  mine: boolean;
  selection: Selection;
  children: ReactNode;
}) {
  const timer = useRef(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const pressed = useRef(false);
  const chosen = selection.has(id);

  const cancel = () => {
    window.clearTimeout(timer.current);
    start.current = null;
  };

  return (
    <div
      className={chosen ? 'selectable chosen' : 'selectable'}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        pressed.current = false;
        start.current = { x: event.clientX, y: event.clientY };
        timer.current = window.setTimeout(() => {
          pressed.current = true;
          selection.toggle(id);
        }, 450);
      }}
      onPointerMove={(event) => {
        const at = start.current;
        if (at && Math.hypot(event.clientX - at.x, event.clientY - at.y) > 8) cancel();
      }}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onContextMenu={(event) => {
        // A right click on a desktop, and the browser's own long-press menu on a
        // phone, are both this app's long press.
        event.preventDefault();
        if (!pressed.current) selection.toggle(id);
        pressed.current = true;
      }}
      onClickCapture={(event) => {
        // The click that ends a long press, or any tap while choosing, is a tick —
        // not a play, a photo or a retry.
        if (pressed.current || selection.active) {
          event.stopPropagation();
          event.preventDefault();
          if (!pressed.current) selection.toggle(id);
        }
        pressed.current = false;
      }}
    >
      <span className={selection.active ? 'select-mark shown' : 'select-mark'} aria-hidden="true">
        <Icon name={chosen ? 'check_circle' : 'radio_button_unchecked'} size={24} />
      </span>
      <div className={selection.active && !mine ? 'selectable-body aside' : 'selectable-body'}>{children}</div>
    </div>
  );
}

/**
 * «Удалить сообщения (N)?» — and, where any of them has reached the other side,
 * whether they go there too.
 */
export function DeleteMessagesSheet({
  count,
  otherPerson,
  anySent,
  busy,
  error,
  onDelete,
  onClose,
}: {
  count: number;
  /** «у водителя», «у поддержки». */
  otherPerson: string;
  anySent: boolean;
  busy: boolean;
  error?: ApiError | null;
  onDelete: (forEveryone: boolean) => void;
  onClose: () => void;
}) {
  const [everyone, setEveryone] = useState(false);
  return (
    <ConfirmSheet
      title={`Удалить сообщения (${count})?`}
      hint="Вы уверены, что хотите удалить выбранные сообщения? Это действие нельзя отменить."
      label="Удалить"
      busy={busy}
      error={error}
      onConfirm={() => onDelete(everyone)}
      onClose={onClose}
    >
      {/* Nothing to ask about a message that never left: there is no copy of it
          anywhere else to delete. */}
      {anySent && (
        <button type="button" className="check-row" onClick={() => setEveryone(!everyone)}>
          <span className={everyone ? 'checkbox on' : 'checkbox'}>
            {everyone && <Icon name="check_rounded" size={14} />}
          </span>
          Также удалить {otherPerson}
        </button>
      )}
    </ConfirmSheet>
  );
}
